// ViewModalChartSection.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ViewModalChartSection.css';

// Components
import Checkbox from 'components/checkbox/Checkbox';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

const CHART_TYPE_OPTIONS = [
    { label: 'Bar', value: 'bar' },
    { label: 'Line', value: 'line' },
    { label: 'Area', value: 'area' },
    { label: 'Pie', value: 'pie' },
];

const AGGREGATION_OPTIONS = [
    { label: 'Count', value: 'count' },
    { label: 'Sum', value: 'sum' },
    { label: 'Average', value: 'avg' },
    { label: 'Minimum', value: 'min' },
    { label: 'Maximum', value: 'max' },
];

const FONT_OPTIONS = [
    { label: 'Use app font', value: 'inherit' },
    { label: 'IBM Plex Sans', value: "'IBM Plex Sans', sans-serif" },
    { label: 'Space Grotesk', value: "'Space Grotesk', sans-serif" },
    { label: 'Source Sans 3', value: "'Source Sans 3', sans-serif" },
    { label: 'Georgia', value: "'Georgia', serif" },
    { label: 'Menlo', value: "'Menlo', monospace" },
];

const Y_AXIS_FORMAT_OPTIONS = [
    { label: 'Number', value: 'number' },
    { label: 'Compact (1.2K)', value: 'compact' },
    { label: 'Currency', value: 'currency' },
    { label: 'Percent', value: 'percent' },
];

const X_DATE_BIN_OPTIONS = [
    { label: 'Auto', value: 'auto' },
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Quarter', value: 'quarter' },
    { label: 'Year', value: 'year' },
];

/**
 * ViewModalChartSection
 *
 * Renders chart configuration controls for chart type, dimensions,
 * formatting, and series-level color overrides.
 *
 * @param {boolean} isVisible - Whether the section should be rendered.
 * @param {object} chartConfig - Current chart configuration values.
 * @param {function} setChartConfig - Function to update chart configuration.
 * @param {array} chartDimensionOptions - Dimension field options for X and split.
 * @param {array} chartMeasureOptions - Measure field options for Y values.
 *
 * @returns {ReactNode} The rendered component.
 */
const ViewModalChartSection = ({
    isVisible,
    chartConfig,
    setChartConfig,
    chartDimensionOptions,
    chartMeasureOptions,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- STATE ----- //

    // Series Key Input

    const [seriesKeyInput, setSeriesKeyInput] = useState('');

    // Series Color Input
    const [seriesColorInput, setSeriesColorInput] = useState('');

    // ----- MEMOS ----- //

    // Memoized series color entries sorted by key.

    const seriesColors = useMemo(() => {
        const source = chartConfig?.seriesColors;
        if (!source || typeof source !== 'object') return [];
        return Object.entries(source)
            .map(([seriesKey, color]) => ({
                color: String(color || '').trim(),
                seriesKey: String(seriesKey || '').trim(),
            }))
            .filter((entry) => entry.seriesKey)
            .sort((a, b) => a.seriesKey.localeCompare(b.seriesKey, undefined, { sensitivity: 'base' }));
    }, [chartConfig?.seriesColors]);

    const aggregation = String(chartConfig?.aggregation || 'count').toLowerCase();
    const requiresMeasure = aggregation !== 'count';
    const yAxisFormat = String(chartConfig?.yAxisFormat || 'number').toLowerCase();
    const selectedXField = String(chartConfig?.xField || '');
    const selectedXFieldType = String(
        chartDimensionOptions.find((option) => option?.value === selectedXField)?.fieldType || ''
    ).toLowerCase();
    const isTemporalXField = selectedXFieldType === 'date'
        || selectedXFieldType === 'datetime'
        || selectedXFieldType === 'daterange'
        || selectedXFieldType === 'datetimerange';

    // ----- UTILITIES ----- //

    /**
     * Updates one chart config key with a new value.
     *
     * @param {string} key - Configuration key to update.
     * @param {any} value - New value for the configuration key.
     */

    const updateChartConfig = (key, value) => {
        setChartConfig((prev) => ({
            ...(prev || {}),
            [key]: value,
        }));
    };

    /**
     * Adds, updates, or removes a series color entry.
     *
     * @param {string} seriesKey - Series key value.
     * @param {string} color - CSS color value for the series.
     */
    const upsertSeriesColor = (seriesKey, color) => {
        const normalizedSeriesKey = String(seriesKey || '').trim();
        if (!normalizedSeriesKey) return;
        const normalizedColor = String(color || '').trim();

        setChartConfig((prev) => {
            const nextSeriesColors = {
                ...((prev?.seriesColors && typeof prev.seriesColors === 'object') ? prev.seriesColors : {}),
            };

            if (!normalizedColor) {
                delete nextSeriesColors[normalizedSeriesKey];
            } else {
                nextSeriesColors[normalizedSeriesKey] = normalizedColor;
            }

            return {
                ...(prev || {}),
                seriesColors: nextSeriesColors,
            };
        });
    };

    // ----- HANDLERS ----- //

    /**
     * Adds a new series color mapping from local inputs.
     */

    const handleAddSeriesColor = () => {
        const normalizedKey = String(seriesKeyInput || '').trim();
        const normalizedColor = String(seriesColorInput || '').trim();
        if (!normalizedKey || !normalizedColor) return;
        upsertSeriesColor(normalizedKey, normalizedColor);
        setSeriesKeyInput('');
        setSeriesColorInput('');
    };

    // ----- UTILITIES ----- //

    /**
     * Removes a series color mapping by key.
     *
     * @param {string} seriesKey - Series key value to remove.
     */

    const removeSeriesColor = (seriesKey) => {
        upsertSeriesColor(seriesKey, '');
    };
    if (!isVisible) return null;

    // ----- RENDER ----- //

    return (
        <div className="view-modal-section">

            <div className="view-modal-section-header">
                <div
                  className="view-modal-section-title"
                  style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Chart
                </div>
            </div>

            <div
              className="view-modal-row"
              style={{
                    gridTemplateColumns: '1fr 1fr',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <div
                      className="view-modal-section-title"
                      style={{
                            color: theme.foregroundMuted,
                            fontSize: 11,
                            paddingBottom: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                        }}
                    >
                        Chart Type
                    </div>
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={chartConfig?.chartType || 'bar'}
                      options={CHART_TYPE_OPTIONS}
                      onChange={(e) => updateChartConfig('chartType', e.target.value)}
                    />
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <div
                      className="view-modal-section-title"
                      style={{
                            color: theme.foregroundMuted,
                            fontSize: 11,
                            paddingBottom: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                        }}
                    >
                        Aggregation
                    </div>
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={chartConfig?.aggregation || 'count'}
                      options={AGGREGATION_OPTIONS}
                      onChange={(e) => updateChartConfig('aggregation', e.target.value)}
                    />
                </div>
            </div>

            <div
              className="view-modal-row"
              style={{
                    gridTemplateColumns: '1fr 1fr',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <div
                      className="view-modal-section-title"
                      style={{
                            color: theme.foregroundMuted,
                            fontSize: 11,
                            paddingBottom: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                        }}
                    >
                        Category Field (X)
                    </div>
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={chartConfig?.xField || ''}
                      options={chartDimensionOptions}
                      placeholder="Category field..."
                      onChange={(e) => updateChartConfig('xField', e.target.value)}
                    />
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <div
                      className="view-modal-section-title"
                      style={{
                            color: theme.foregroundMuted,
                            fontSize: 11,
                            paddingBottom: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                        }}
                    >
                        Series Split
                    </div>
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={chartConfig?.groupField || ''}
                      options={[
                            { value: '', label: 'No series split' },
                            ...chartDimensionOptions,
                        ]}
                      onChange={(e) => updateChartConfig('groupField', e.target.value)}
                    />
                </div>
            </div>

            <div
              className="view-modal-row"
              style={{
                    gridTemplateColumns: '1fr',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <div
                      className="view-modal-section-title"
                      style={{
                            color: theme.foregroundMuted,
                            fontSize: 11,
                            paddingBottom: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                        }}
                    >
                        Value Field (Y)
                    </div>
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      disabled={!requiresMeasure}
                      value={chartConfig?.yField || ''}
                      options={chartMeasureOptions}
                      placeholder={requiresMeasure ? 'Value field...' : 'Not needed for Count'}
                      onChange={(e) => updateChartConfig('yField', e.target.value)}
                    />
                </div>
            </div>

            <div
              className="view-modal-row"
              style={{
                    gridTemplateColumns: '1fr',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <div
                      className="view-modal-section-title"
                      style={{
                            color: theme.foregroundMuted,
                            fontSize: 11,
                            paddingBottom: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                        }}
                    >
                        Font Family
                    </div>
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={chartConfig?.fontFamily || 'inherit'}
                      options={FONT_OPTIONS}
                      onChange={(e) => updateChartConfig('fontFamily', e.target.value)}
                    />
                </div>
            </div>

            <div
              className="view-modal-row"
              style={{
                    gridTemplateColumns: '1fr 1fr',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <div
                      className="view-modal-section-title"
                      style={{
                            color: theme.foregroundMuted,
                            fontSize: 11,
                            paddingBottom: 4,
                            paddingLeft: 4,
                            paddingTop: 4,

                        }}
                    >
                        Y-Axis Format
                    </div>
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      value={chartConfig?.yAxisFormat || 'number'}
                      options={Y_AXIS_FORMAT_OPTIONS}
                      onChange={(e) => updateChartConfig('yAxisFormat', e.target.value)}
                    />
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        minWidth: 0,
                    }}
                >
                    <div
                      className="view-modal-section-title"
                      style={{
                            color: theme.foregroundMuted,
                            fontSize: 11,
                            paddingBottom: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                        }}
                    >
                        Date Bin (X)
                    </div>
                    <SelectInput
                      style={{
                            fontSize: '12px',
                        }}
                      disabled={!isTemporalXField}
                      value={chartConfig?.xDateBin || 'auto'}
                      options={X_DATE_BIN_OPTIONS}
                      onChange={(e) => updateChartConfig('xDateBin', e.target.value)}
                    />
                </div>
            </div>

            <div
              className="view-modal-row"
              style={{
                    gridTemplateColumns: '1fr 1fr',
                }}
            >
                <Checkbox
                  fontSize={12}
                  label="Show legend"
                  size={24}
                  value={Boolean(chartConfig?.showLegend)}
                  onChange={() => updateChartConfig('showLegend', !chartConfig?.showLegend)}
                />
                <Checkbox
                  fontSize={12}
                  label="Show grid"
                  size={24}
                  value={Boolean(chartConfig?.showGrid)}
                  onChange={() => updateChartConfig('showGrid', !chartConfig?.showGrid)}
                />
            </div>

            {yAxisFormat === 'currency' && (
                <div
                  className="view-modal-row"
                  style={{
                        gridTemplateColumns: '1fr',
                    }}
                >
                    <TextInput
                      maxLength={6}
                      placeholder="Currency code (USD, EUR, GBP...)"
                      value={chartConfig?.currencyCode || 'USD'}
                      onChange={(e) => updateChartConfig('currencyCode', String(e.target.value || '').toUpperCase())}
                    />
                </div>
            )}

            <div
              className="view-modal-section-title"
              style={{
                    color: theme.foregroundMuted,
                    fontSize: 11,
                    paddingBottom: 4,
                    paddingLeft: 4,
                    paddingTop: 4,
                }}
            >
                Colors
            </div>

            <div
              className="view-modal-row"
              style={{
                    gridTemplateColumns: '1fr 1fr 1fr',
                }}
            >
                <TextInput
                  style={{
                        fontSize: '12px',
                    }}
                  maxLength={20}
                  placeholder="Background hex"
                  value={chartConfig?.backgroundColor || ''}
                  onChange={(e) => updateChartConfig('backgroundColor', e.target.value)}
                />
                <TextInput
                  style={{
                        fontSize: '12px',
                    }}
                  maxLength={20}
                  placeholder="Text color"
                  value={chartConfig?.textColor || ''}
                  onChange={(e) => updateChartConfig('textColor', e.target.value)}
                />
                <TextInput
                  style={{
                        fontSize: '12px',
                    }}
                  maxLength={20}
                  placeholder="Grid color"
                  value={chartConfig?.gridColor || ''}
                  onChange={(e) => updateChartConfig('gridColor', e.target.value)}
                />
            </div>

            <div className="view-modal-section-header">
                <div
                  className="view-modal-section-title"
                  style={{
                        color: theme.foregroundMuted,
                        paddingBottom: 4,
                        paddingLeft: 4,
                        paddingTop: 4,
                    }}
                >
                    Series Colors
                </div>
            </div>

            <div
              className="view-modal-row"
              style={{
                    gridTemplateColumns: '1fr 1fr auto',
                }}
            >
                <TextInput
                  style={{
                        fontSize: '12px',
                    }}
                  maxLength={60}
                  placeholder="Series value"
                  value={seriesKeyInput}
                  onChange={(e) => setSeriesKeyInput(e.target.value)}
                />
                <TextInput
                  style={{
                        fontSize: '12px',
                    }}
                  maxLength={20}
                  placeholder="Color (hex/css)"
                  value={seriesColorInput}
                  onChange={(e) => setSeriesColorInput(e.target.value)}
                />
                {/* ACTION BUTTON */}
                <button
                  className="view-modal-action-button"
                  disabled={!seriesKeyInput.trim() || !seriesColorInput.trim()}
                  type="button"
                  onClick={handleAddSeriesColor}
                >
                    Add
                </button>
            </div>

            {seriesColors.map((entry) => (
                <div
                  className="view-modal-row"
                  style={{
                        gridTemplateColumns: '1fr 1fr auto',
                    }}
                  key={entry.seriesKey}
                >
                    <TextInput
                      disabled
                      value={entry.seriesKey}
                    />
                    <TextInput
                      maxLength={20}
                      value={entry.color}
                      onChange={(e) => upsertSeriesColor(entry.seriesKey, e.target.value)}
                    />
                    {/* ACTION BUTTON */}
                    <button
                      className="view-modal-action-button"
                      type="button"
                      onClick={() => removeSeriesColor(entry.seriesKey)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );
};

// Dev Tools
ViewModalChartSection.displayName = 'ViewModalChartSection';

export default ViewModalChartSection;
